// The ECONIC vocabulary the primitives draw (DESIGN-SYSTEM §8, §10.10; SPEC §4.5, §13). WP-THEME.
// Glyphs and names of the six agents, the nine desk glyphs, and the exact policy names of the four markings.
// Bilingual brand and policy names are fixed data, not interface copy: they render in both languages from here so that every screen
// says them the same way (one value per fact). Arabic follows scripts/check-locales.mjs (no diacritics). All Arabic is pending MoET
// review (dep, DESIGN-SYSTEM §20).
import {
  BarChart3,
  BookOpen,
  CalendarClock,
  Database,
  FilePenLine,
  Gauge,
  Globe,
  Landmark,
  LifeBuoy,
  Microscope,
  Network,
  Presentation,
  Radar,
  Scale,
  Table2,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import type { DeskNumber, EconicModuleKey, Localised, Sensitivity } from "./types";

export interface EconicModuleInfo {
  key: Exclude<EconicModuleKey, "knowledge">;
  icon: LucideIcon;
  /** The agent's name in the Agents menu, tiles, tags and search (16 Oct 2026: one name per agent everywhere). */
  short: Localised;
  /** The product's name (Knowledge Bank, Economic Radar…); the same as `short`, so the menu, the tile and the page never disagree. */
  name: Localised;
  /** The agent's persona, its first name everywhere since 21 Oct 2026 ("The Librarian", with "Knowledge Bank" under it). Policy (PMO),
   *  supplied by the Prime Minister's Office, has none and keeps its name. */
  persona?: Localised;
}

/**
 * The six agents in the Ministry's order (16 Oct 2026): Knowledge Bank · Economic Radar · Country360 · Policy (PMO) · Economic Modelling ·
 * News Watch. Policy (PMO) is the policy agent supplied by the Prime Minister's Office. The keys never change.
 */
export const ECONIC_MODULES: readonly EconicModuleInfo[] = [
  { key: "kb", icon: Database, short: { en: "Knowledge Bank", ar: "بنك المعرفة" }, name: { en: "Knowledge Bank", ar: "بنك المعرفة" }, persona: { en: "The Librarian", ar: "أمين المكتبة" } },
  { key: "uae-radar", icon: BarChart3, short: { en: "Economic Radar", ar: "الرادار الاقتصادي" }, name: { en: "Economic Radar", ar: "الرادار الاقتصادي" }, persona: { en: "The Economist", ar: "الخبير الاقتصادي" } },
  { key: "country360", icon: Network, short: { en: "Country360", ar: "الدول 360" }, name: { en: "Country360", ar: "الدول 360" }, persona: { en: "The Country Analyst", ar: "محلل الدول" } },
  { key: "policylab", icon: Landmark, short: { en: "Policy (PMO)", ar: "السياسات (PMO)" }, name: { en: "Policy (PMO)", ar: "السياسات (PMO)" } },
  { key: "outlook", icon: TrendingUp, short: { en: "Economic Modelling", ar: "النمذجة الاقتصادية" }, name: { en: "Economic Modelling", ar: "النمذجة الاقتصادية" }, persona: { en: "The Modeller", ar: "مختص النمذجة" } },
  { key: "global-radar", icon: Globe, short: { en: "News Watch", ar: "رصد الأخبار" }, name: { en: "News Watch", ar: "رصد الأخبار" }, persona: { en: "The Scout", ar: "راصد الإشارات" } },
];

/** An agent's two names (MoET, 21 Oct 2026): the persona as its name, the product under it — "The Librarian" over "Knowledge Bank" —
 *  on its page, in the Agents menu, its tiles and its conversations. Policy (PMO) has no persona: its name stays and `sub` is null (the
 *  caller shows who supplies it). `other` is the name in the other language. */
export function agentNames(key: string, ar: boolean): { name: string; sub: string | null; other: string } {
  const m = ECONIC_MODULES.find((x) => x.key === (key === "knowledge" ? "kb" : key));
  if (!m) return { name: key, sub: null, other: "" };
  const first = m.persona ?? m.name;
  return { name: ar ? first.ar : first.en, sub: m.persona ? (ar ? m.name.ar : m.name.en) : null, other: ar ? first.en : first.ar };
}

/** Looks a module up by its UI key; surfaces and catalogues call the Knowledge Bank `knowledge`, which maps to `kb`. */
export function econicModule(key: EconicModuleKey): EconicModuleInfo {
  const k = key === "knowledge" ? "kb" : key;
  return ECONIC_MODULES.find((m) => m.key === k) ?? ECONIC_MODULES[0]!;
}

/** Desk glyphs 1–9 (DESIGN-SYSTEM §8): studies, international reports, statistical report, quarterly report, publications, open data,
 *  scientific activities, early warning, recovery exercises. */
export const DESK_GLYPHS: Readonly<Record<DeskNumber, LucideIcon>> = {
  1: Microscope,
  2: Globe,
  3: Table2,
  4: CalendarClock,
  5: BookOpen,
  6: Database,
  7: Presentation,
  8: Radar,
  9: LifeBuoy,
};

/** The role specialists' glyphs (the roster's `specialists`, Roster.cs), one map for the Specialists pane, the coverage popover and the
 *  faces generated for them. */
export const SPECIALIST_GLYPHS: Readonly<Record<string, LucideIcon>> = {
  "evidence-researcher": Microscope,
  "horizon-scanner": Radar,
  "policy-drafter": FilePenLine,
  "legislation-review": Scale,
  "kpi-monitor": Gauge,
  "international-digest": Globe,
};

/** A glyph by the Lucide name the server sends (`icon` of a specialist or an assistant), for the glyphs ECONIC draws; null otherwise. */
const NAMED_GLYPHS: Readonly<Record<string, LucideIcon>> = {
  BarChart3, BookOpen, CalendarClock, Database, FilePenLine, Gauge, Globe, Landmark, LifeBuoy, Microscope, Network, Presentation, Radar,
  Scale, Table2, TrendingUp,
};
export function glyphByName(name: string | null | undefined): LucideIcon | null {
  if (!name) return null;
  const pascal = name.replace(/(^|[-_ ])([a-z0-9])/g, (_m, _s, c: string) => c.toUpperCase());
  return NAMED_GLYPHS[name] ?? NAMED_GLYPHS[pascal] ?? null;
}

/** The four markings of the Ministry's data-classification policy, lowest first (ECN-135), with their exact names. */
export const SENSITIVITY_ORDER: readonly Sensitivity[] = ["public", "internal", "restricted", "confidential"];
export const SENSITIVITY_NAMES: Readonly<Record<Sensitivity, Localised>> = {
  public: { en: "Public", ar: "عام" },
  internal: { en: "Internal", ar: "داخلي" },
  restricted: { en: "Restricted", ar: "مقيد" },
  confidential: { en: "Confidential", ar: "سري" },
};

/** The higher of two markings (the banner shows the highest on screen). */
export function maxSensitivity(a: Sensitivity, b: Sensitivity): Sensitivity {
  return SENSITIVITY_ORDER.indexOf(a) >= SENSITIVITY_ORDER.indexOf(b) ? a : b;
}
